export interface TableRowUpdateConfig<T = any> {
  /** 获取表格数据列表 */
  getTableData: () => T[]
  /** 获取查询参数（用于请求数据） */
  getQueryParams: () => any
  /** 请求单行数据的 API 函数，接收计算出的 page 和 queryParams，返回包含 list 的响应对象 */
  fetchRowApi: (page: number, queryParams: any) => Promise<{ list: T[] }>
  /** 更新单行数据（可保留选中状态等特殊属性） */
  updateRow?: (oldRow: T, newRow: T) => T
  /** 需要同步的目标列表（单个或多个），按 index 匹配更新 */
  syncTargetList?: (() => T[]) | (() => T[])[]
}

/**
 * 表格单行操作后数据更新 Composable（基于 index）
 *
 * @example
 * // 基础用法
 * const { updateRowData } = useTableRowUpdate({
 *   getTableData: () => tableData.value,
 *   getQueryParams: () => queryParams,
 *   fetchRowApi: (page, queryParams) =>
 *     service.getList({ ...queryParams, page, limit: 1 })
 * })
 */
export function useTableRowUpdate<T = any>(config: TableRowUpdateConfig<T>) {
  const {
    getTableData,
    getQueryParams,
    fetchRowApi,
    updateRow = (oldRow, newRow) => ({ ...oldRow, ...newRow }),
    syncTargetList
  } = config

  /**
   * 根据 index 更新单行数据：通过 index 计算页码（limit=1），请求数据并更新
   */
  function updateRowData(rowIndex: number): Promise<void> {
    const tableData = getTableData()

    if (rowIndex < 0 || rowIndex >= tableData.length) {
      return Promise.resolve()
    }

    const queryParams = getQueryParams()
    const page = rowIndex + 1

    return fetchRowApi(page, queryParams).then(response => {
      if (!response?.list?.length) return

      // 直接使用返回的第一条数据（因为 limit=1）
      const updatedItem = response.list[0]

      const currentTableData = getTableData()
      if (rowIndex >= currentTableData.length) return

      currentTableData[rowIndex] = updateRow(currentTableData[rowIndex], updatedItem)

      if (syncTargetList) {
        const targetLists = Array.isArray(syncTargetList) ? syncTargetList.map(fn => fn()) : [syncTargetList()]

        targetLists.forEach(targetList => {
          if (rowIndex < targetList.length) {
            targetList[rowIndex] = currentTableData[rowIndex]
          }
        })
      }
    }).catch(error => {
      console.error('更新单行数据失败:', error)
    })
  }

  return {
    updateRowData
  }
}

